/**
 * Wizard step 5, Start (W2-14 onboarding-wizard; DESIGN.md §4.3 "(5) START", §5.1 steps 6-7).
 *
 * Cards with animated previews (a note falling onto the line; still under reduced motion): One line (the classic
 * start), Two lines, Empty and, at the Full level, RPE-style 24 lines. Below them a summary of the new chart, the
 * Problems reminders for skipped steps (unconfirmed timing, placeholder picture, skipped details) and, on the first
 * project, the one sentence explaining why the browser may ask to keep the site's data. Create is in the footer.
 */

import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { KeymapService, WizardStepProps } from '../../../app/registry/types';
import type { Json } from '../../../core/util/types';
import { Icon } from '../../../ui';
import { isFirstProject } from '../createProject';
import { cleanText, formatDuration, formatLevel } from '../draft';
import { START_LAYOUTS, type StartLayout } from '../startLayouts';
import { m } from '../wizard.messages';
import s from '../Wizard.module.css';
import { useServicesText, useSettingsValue, useStepActions } from '../wizardSession';

const LAYOUT_TEXT: Readonly<Record<StartLayout, { readonly title: string; readonly body: string }>> = {
  oneLine: { title: m.layoutOneLine, body: m.layoutOneLineBody },
  twoLines: { title: m.layoutTwoLines, body: m.layoutTwoLinesBody },
  empty: { title: m.layoutEmpty, body: m.layoutEmptyBody },
  rpe24: { title: m.layoutRpe24, body: m.layoutRpe24Body },
};

/** Small 16:9 picture of a layout (lines as strokes, a falling note on the first line). */
function LayoutPicture(props: { readonly layout: StartLayout }) {
  const { layout } = props;
  const lines: Array<{ readonly y: number; readonly faint: boolean }> =
    layout === 'oneLine'
      ? [{ y: 62, faint: false }]
      : layout === 'twoLines'
        ? [
            { y: 62, faint: false },
            { y: 30, faint: false },
          ]
        : layout === 'rpe24'
          ? [{ y: 62, faint: false }, ...Array.from({ length: 6 }, (_, i) => ({ y: 12 + i * 7, faint: true }))]
          : [];
  return (
    <svg className={s.layoutPicture} viewBox="0 0 160 90" aria-hidden="true" focusable="false">
      <rect className={s.layoutFrame} x="0.5" y="0.5" width="159" height="89" rx="4" />
      {lines.map((l, i) => (
        <line key={i} className={l.faint ? s.layoutLineFaint : s.layoutLine} x1="30" x2="130" y1={l.y} y2={l.y} />
      ))}
      {layout === 'empty' ? (
        <g className={s.layoutPlus}>
          <line x1="80" x2="80" y1="36" y2="54" />
          <line x1="71" x2="89" y1="45" y2="45" />
        </g>
      ) : (
        <rect className={s.layoutNote} x="70" y="-6" width="20" height="4" rx="1" />
      )}
    </svg>
  );
}

/**
 * Label of the key the keymap binds to the Song & Timing workspace now (`app.workspace { workspace: 'timing' }`),
 * or 'none' (texts never write keys themselves).
 */
export function timingKeyLabel(keymap: Pick<KeymapService, 'bindings' | 'label'>): string {
  for (const b of keymap.bindings()) {
    if (b.command !== 'app.workspace' || b.remove === true) continue;
    const a = b.args;
    if (a === null || typeof a !== 'object' || Array.isArray(a)) continue;
    if ((a as { readonly [k: string]: Json }).workspace !== 'timing') continue;
    try {
      return keymap.label(b.chord);
    } catch {
      return b.chord;
    }
  }
  return 'none';
}

export function StartStep(props: WizardStepProps) {
  const { services, draft } = props;
  const { update, setCanContinue } = useStepActions(props);
  const text = useServicesText(services);
  const settings = useSettingsValue(services);
  const [first, setFirst] = useState(false);
  const refs = useRef(new Map<StartLayout, HTMLButtonElement>());

  useEffect(() => {
    setCanContinue(draft.music !== null);
  }, [draft.music, setCanContinue]);

  useEffect(() => {
    let live = true;
    void isFirstProject(services).then((f) => {
      if (live) setFirst(f);
    });
    return () => {
      live = false;
    };
  }, [services]);

  const full = settings.general.level === 'full';
  const timingKey = timingKeyLabel(services.keymap);
  const layouts = START_LAYOUTS.filter((l) => full || l.level === 'guided' || l.id === draft.start);

  const pick = (id: StartLayout): void => {
    if (id !== draft.start) update({ start: id });
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const i = layouts.findIndex((l) => l.id === draft.start);
    const forward = e.key === 'ArrowRight' || e.key === 'ArrowDown';
    const next = layouts[(i + (forward ? 1 : -1) + layouts.length) % layouts.length];
    if (next === undefined) return;
    pick(next.id);
    refs.current.get(next.id)?.focus();
  };

  const skippedPicture = draft.illustration === null;
  const skippedBeat = !draft.timingConfirmed;
  const skippedDetails = draft.skipped.includes('details');
  const offsetSec = Math.round(draft.offsetMs) / 1000;

  return (
    <div className={s.step}>
      <p className={s.lead}>{text.t(m.startIntro)}</p>
      <div className={s.layouts} role="radiogroup" aria-label={text.t(m.layoutsLabel)} onKeyDown={onKeyDown}>
        {layouts.map((l) => {
          const checked = l.id === draft.start;
          const t = LAYOUT_TEXT[l.id];
          return (
            <button
              key={l.id}
              ref={(el) => {
                if (el === null) refs.current.delete(l.id);
                else refs.current.set(l.id, el);
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              className={s.layoutCard}
              data-checked={checked ? 'true' : undefined}
              onClick={() => {
                pick(l.id);
              }}
            >
              <LayoutPicture layout={l.id} />
              <span className={s.layoutTitle}>
                {checked && <Icon name="CircleCheck" size={14} />}
                {text.t(t.title)}
              </span>
              <span className={s.layoutBody}>{text.t(t.body)}</span>
            </button>
          );
        })}
      </div>

      <section className={s.summary} aria-label={text.t(m.summaryTitle)}>
        <h3 className={s.summaryTitle}>{text.t(m.summaryTitle)}</h3>
        <ul className={s.summaryList}>
          {draft.music !== null && (
            <li>
              <Icon name="Music" size={14} />
              {text.t(m.summarySong, { name: draft.music.name, duration: formatDuration(draft.music.durationSec) })}
            </li>
          )}
          <li>
            <Icon name="Tag" size={14} />
            {text.t(m.summaryDetails, {
              title: cleanText(draft.name) === '' ? text.t(m.untitled) : cleanText(draft.name),
              level: formatLevel(draft.levelLabel, draft.difficulty),
            })}
          </li>
          <li>
            <Icon name="Metronome" size={14} />
            {text.t(draft.timingConfirmed ? m.summaryTiming : m.summaryTimingUnconfirmed, {
              bpm: Math.round(draft.bpm * 1000) / 1000,
              offset: offsetSec,
            })}
          </li>
        </ul>
        {(skippedPicture || skippedBeat || skippedDetails) && (
          <div className={s.reminders}>
            <p className={s.remindTitle}>
              <Icon name="Lightbulb" size={14} />
              {text.t(m.remindTitle)}
            </p>
            <ul>
              {skippedBeat && <li>{text.t(m.remindBeat, { key: timingKey })}</li>}
              {skippedPicture && <li>{text.t(m.remindPicture)}</li>}
              {skippedDetails && <li>{text.t(m.remindDetails)}</li>}
            </ul>
          </div>
        )}
        {first && (
          <p className={s.verdict} data-kind="info">
            <Icon name="ShieldCheck" size={16} />
            <span>{text.t(m.persistNote)}</span>
          </p>
        )}
      </section>
    </div>
  );
}
